import DOMPurify from "dompurify";
import { toastr } from "react-redux-toastr";

type ToastOptions = Record<string, unknown>;
type UnsafePayloadPath = Array<string | number>;
type UnsafePayloadCollectionOptions = {
  shouldIgnorePath?: (_path: UnsafePayloadPath) => boolean;
};

const SQL_INJECTION_PATTERNS: RegExp[] = [
  /\bselect\s+(?:\*|[\w`]+(?:\s*,\s*[\w`]+)*)\s+from\s+[\w`]+(?:\s+(?:where|join|order\s+by|group\s+by|limit)\b|;|$)/i,
  /\b(?:insert\s+into|update\s+[\w`]+\s+set|delete\s+from|drop\s+table|alter\s+table|truncate\s+table|create\s+table)\b/i,
  /\bselect\s+(?:\*|\d+|['"`][^'"`]+['"`])\s*;/i,
  /(?:\bunion(?:\s+all)?\s+select\b[\s\S]{0,200}(?:--|#|\/\*|$))/i,
  /(?:;\s*(?:select|drop|union|insert|update|delete|alter|exec|execute)\b[\s\S]{0,200}(?:--|#|;|$))/i,
  /(?:\b(?:or|and)\b\s+[\w'"`]+\s*=\s*[\w'"`]+\s*(?:--|#)?)/i,
  /(?:\b(?:or|and)\b\s+['"]?\d+['"]?\s*=\s*['"]?\d+['"]?\s*(?:--|#)?)/i,
  /(?:\b(?:or|and)\b\s+['"][^'"]+['"]\s*=\s*['"][^'"]+['"]\s*(?:--|#)?)/i,
  /(?:\b(?:or|and)\b\s+[\w'"`]+\s*like\s*[\w'"`%]+)/i,
  /['"`]\s*(?:or|and)\s+(?:true|false)\b(?:\s*(?:--|#|\/\*)|\s*$)/i,
  /(?:--\s*(?:select|union|insert|update|delete|drop|alter|truncate|create|rename|replace|grant|revoke|exec|execute|or|and|where|from|into|table|values|set)\b)/i,
  /(?:\/\*[\s\S]{0,200}\*\/)/i,
  /(?:;\s*(?:select|drop|union|insert|update|delete|alter|exec|execute)\b)/i,
  /\bwaitfor\s+delay\s+['"]?\d{1,2}:\d{1,2}:\d{1,2}['"]?/i,
  /\b(?:sleep|benchmark|pg_sleep)\s*\(/i,
  /\b(?:extractvalue|updatexml|version|database|user)\s*\(/i,
  /\b(?:information_schema|sysobjects|syscolumns|mysql\.|pg_catalog)\b/i,
  /\b(?:concat|char)\s*\(/i,
];

const SCRIPT_INJECTION_PATTERNS: RegExp[] = [
  /<\s*\/?\s*[a-z][\w:-]*(?:\s[^<>]*)?>/i,
  /<\s*script\b[^>]*>[\s\S]*?<\s*\/\s*script\s*>/i,
  /<\s*iframe\b[^>]*>[\s\S]*?<\s*\/\s*iframe\s*>/i,
  /<\s*svg\b[^>]*>[\s\S]*?<\s*\/\s*svg\s*>/i,
  /<\s*svg\b[^>]*\bon\w+\s*=/i,
  /<\s*(?:img|div|a|span|button)\b[^>]*(?:on\w+|srcdoc|xlink:href)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/i,
  /\b(?:javascript|vbscript|data):/i,
  /\b(?:eval|alert|confirm|prompt|document\.cookie|localStorage|sessionStorage)\s*\(/i,
  /\b(?:new\s+Function|setTimeout|setInterval|fetch)\s*\(/i,
  /\bon\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/i,
  /\b(?:fromcharcode|atob|btoa)\s*\(/i,
  /<\s*\w+\b[^>]*\bon\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/i,
];

const UNSAFE_INPUT_PATTERNS: RegExp[] = [
  ...SQL_INJECTION_PATTERNS,
  ...SCRIPT_INJECTION_PATTERNS,
];

/**
 * @method sanitizeText
 * @description Sanitize text by stripping all HTML tags
 * @param {string} text - Text to sanitize
 * @returns {string} Plain text without HTML
 */
export const sanitizeText = (text: unknown): string => {
  if (!text || typeof text !== "string") return "";
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
};

/**
 * @method sanitizeHTML
 * @description Sanitize HTML with allowed tags for rich content
 * @param {string} html - HTML string to sanitize
 * @returns {string} Sanitized HTML
 */
export const sanitizeHTML = (html: unknown): string => {
  if (!html || typeof html !== "string") return "";
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "em",
      "b",
      "i",
      "u",
      "span",
      "div",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "ul",
      "ol",
      "li",
      "a",
      "img",
    ],
    ALLOWED_ATTR: ["href", "src", "alt", "title", "class", "id"],
  });
};

/**
 * @method sanitizeIframeHTML
 * @description Sanitize iframe HTML string
 * @param {string} htmlString - HTML string to sanitize
 * @returns {string} Sanitized HTML string
 */
export const sanitizeIframeHTML = (htmlString: unknown): string => {
  if (!htmlString || typeof htmlString !== "string") return "";
  return DOMPurify.sanitize(htmlString, {
    ALLOWED_TAGS: ["iframe"],
    ALLOWED_ATTR: [
      "src",
      "width",
      "height",
      "frameborder",
      "allowfullscreen",
      "loading",
      "allow",
      "sandbox",
      "referrerpolicy",
    ],
    FORBID_TAGS: ["script", "object", "embed", "form"],
    FORBID_ATTR: ["onerror", "onload", "onclick"],
  });
};

export const containsSqlInjectionPattern = (value: unknown): boolean => {
  if (!value || typeof value !== "string") return false;

  return SQL_INJECTION_PATTERNS.some((pattern) => pattern.test(value));
};

export const containsUnsafePayload = (value: unknown): boolean => {
  if (!value || typeof value !== "string") return false;

  return UNSAFE_INPUT_PATTERNS.some((pattern) => pattern.test(value));
};

export const sanitizeSqlInput = (value: unknown): unknown => {
  if (!value || typeof value !== "string") return value;

  return value
    .replace(/--.*$/gm, " ")
    .replace(/\/\*[\s\S]*?\*\//g, " ")
    .replace(
      /\b(?:select|union|insert|update|delete|drop|alter|truncate|create|exec|execute|from|where|into|values|set|table|join|on|or|and)\b/gi,
      " "
    )
    .replace(/\*+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

export const showUnsafePayloadToast = (toastOptions: ToastOptions = {}) => {
  toastr.error(
    "Invalid input",
    "SQL-like input, HTML markup, or script content is not allowed.",
    {
      id: "unsafe-input-toast",
      timeOut: 4000,
      ...toastOptions,
    }
  );
};

export const showSqlInjectionToast = (toastOptions: ToastOptions = {}) => {
  showUnsafePayloadToast(toastOptions);
};

const isPlainObject = (value: object): value is Record<string, unknown> => {
  const prototype = Object.getPrototypeOf(value);
  return prototype === null || Object.getPrototypeOf(prototype) === null;
};

const collectUnsafePayloadCandidates = (
  value: unknown,
  results: string[] = [],
  path: UnsafePayloadPath = [],
  options: UnsafePayloadCollectionOptions = {},
  seen: WeakSet<object> = new WeakSet()
): string[] => {
  if (typeof value === "string") {
    if (options.shouldIgnorePath?.(path)) {
      return results;
    }

    if (containsUnsafePayload(value)) {
      results.push(value);
    }
    return results;
  }

  if (Array.isArray(value)) {
    if (seen.has(value)) return results;
    seen.add(value);
    value.forEach((item, index) =>
      collectUnsafePayloadCandidates(
        item,
        results,
        [...path, index],
        options,
        seen
      )
    );
    return results;
  }

  if (value && typeof value === "object") {
    if (!isPlainObject(value)) {
      return results;
    }

    if (seen.has(value)) return results;
    seen.add(value);
    Object.entries(value).forEach(([key, item]) =>
      collectUnsafePayloadCandidates(
        item,
        results,
        [...path, key],
        options,
        seen
      )
    );
  }

  return results;
};

export const hasSqlInjectionPayload = (
  values: unknown,
  options: UnsafePayloadCollectionOptions = {}
): boolean => {
  return collectUnsafePayloadCandidates(values, [], [], options).length > 0;
};

export const hasUnsafePayload = (
  values: unknown,
  options: UnsafePayloadCollectionOptions = {}
): boolean => {
  return collectUnsafePayloadCandidates(values, [], [], options).length > 0;
};

export const shouldBlockUnsafeSubmission = (
  values: unknown,
  toastOptions: ToastOptions = {},
  options: UnsafePayloadCollectionOptions = {}
): boolean => {
  if (hasUnsafePayload(values, options)) {
    showUnsafePayloadToast(toastOptions);
    return true;
  }

  return false;
};

export const shouldBlockUnsafeSearchInput = (
  value: unknown,
  toastOptions: ToastOptions = {}
): boolean => {
  if (containsUnsafePayload(value)) {
    showUnsafePayloadToast(toastOptions);
    return true;
  }

  return false;
};
